import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { callsignProblem, parseCallsigns } from '@/lib/callsigns';

interface CallsignsFieldProps {
  id: string;
  /** The raw text as typed ("NBCU, SDM3"); parse it with parseCallsigns() on save. */
  value: string;
  onChange: (value: string) => void;
  /** Shown in the placeholder, e.g. "NBCU". */
  example?: string;
  disabled?: boolean;
  /** Replaces the default help line (e.g. to say who may edit it). */
  help?: string;
}

/**
 * Universal search shorthand for one record. Keeps the raw text while typing
 * (no reformatting under the cursor) and previews the stored form below.
 */
export function CallsignsField({ id, value, onChange, example, disabled, help }: CallsignsFieldProps) {
  const parsed = parseCallsigns(value);
  const problem = callsignProblem(parsed);
  return (
    <div>
      <Label htmlFor={id}>Callsigns</Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={example ? `e.g. ${example}` : 'e.g. NBCU'}
        disabled={disabled}
        autoComplete="off"
        className="bg-background border-border font-mono"
      />
      <p className="mt-1 text-xs text-muted-foreground">
        {help ?? 'Shorthand people search by. Separate several with commas.'}
      </p>
      {parsed.length > 0 && (
        <div data-testid="callsign-preview" className="mt-1.5 flex flex-wrap gap-1">
          {parsed.map((c) => (
            <span key={c} className="rounded border border-border px-1.5 py-0.5 font-mono text-xs text-foreground">
              {c}
            </span>
          ))}
        </div>
      )}
      {problem && (
        <p role="alert" className="mt-1 text-xs text-[#E0556C]">{problem}</p>
      )}
    </div>
  );
}
